import React, { useState } from 'react';
import {
    Dropdown,
    DropdownMenu,
    DropdownToggle,
} from 'reactstrap';
import { TFeature } from 'services/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { useGetOrganizationAddons } from 'hooks/useGetOrganizationAddons';
import useAuthContext from 'contexts/AuthContext';
import { useOrganizationAccess } from 'hooks/useOrganizationAccess';
import {
    PORTFOLIO_ANALYTICS_PRO_ADDON_ID,
    MENU_SECTIONS,
    DEVELOPMENT_PROJECTS_SUBSECTION,
    INVOICES_SUBSECTION,
    ANALYTICS_BENCHMARK_SUBSECTION,
    ANALYTICS_FORECAST_SUBSECTION,
} from 'components/NSNavbar/constants';
import RenderIf from 'components/RenderIf/RenderIf';
import MenuSection from './components/MenuSection';

const PowerToolsMenu = () => {
    const { selectedOrganizationId } = useAuthContext();
    const { data: addons, isSuccess } = useGetOrganizationAddons(selectedOrganizationId!);
    const { hasCoreAccess } = useOrganizationAccess();

    const [activePopover, setActivePopover] = useState<TFeature | null>(null);
    const [isPowerToolsMenuOpen, setIsPowerToolsMenuOpen] = useState<boolean>(false);

    const toggleActivePopover = (feature: TFeature) => {
        if (activePopover === feature) {
            setActivePopover(null);
        } else {
            setActivePopover(feature);
        }
    };

    const togglePowerToolsMenu = () => {
        setIsPowerToolsMenuOpen(!isPowerToolsMenuOpen);
        setActivePopover(null);
    };

    const isPAPEnabled = isSuccess && addons.some(addon => addon.id === PORTFOLIO_ANALYTICS_PRO_ADDON_ID);

    const getDevelopmentSubsections = () => {
        const subsections = [];
        if (hasCoreAccess) {
            subsections.push(DEVELOPMENT_PROJECTS_SUBSECTION);
        }
        subsections.push(INVOICES_SUBSECTION);
        return subsections;
    };

    const getAnalyticsSubsections = () => {
        const subsections = [ANALYTICS_BENCHMARK_SUBSECTION];
        if (isPAPEnabled) {
            subsections.push(ANALYTICS_FORECAST_SUBSECTION);
        }
        return subsections;
    };

    return (
        <Dropdown
            isOpen={isPowerToolsMenuOpen}
            toggle={togglePowerToolsMenu}
            onMouseEnter={togglePowerToolsMenu}
            onMouseLeave={togglePowerToolsMenu}
            data-testid="power-tools-menu-dropdown"
            className="d-flex align-items-center px-2"
        >
            <DropdownToggle tag="span" data-testid="power-tools-menu-toggle" className="NSNavbar__dropdown_toggle">
                Power Tools
                <FontAwesomeIcon
                    className="icon ml-2"
                    icon={faCaretDown}
                />
            </DropdownToggle>

            {isPowerToolsMenuOpen && (
                <DropdownMenu className="NSNavbar__dropdown_menu">
                    {/* Northspyre Development */}
                    <MenuSection
                        title={MENU_SECTIONS[0].title}
                        subsections={getDevelopmentSubsections()}
                        hasBorder
                        toggleActivePopover={toggleActivePopover}
                        activePopover={activePopover}
                    />

                    {/* Approvals */}
                    <MenuSection
                        title={MENU_SECTIONS[1].title}
                        items={MENU_SECTIONS[1].items}
                        hasBorder
                        toggleActivePopover={toggleActivePopover}
                        activePopover={activePopover}
                    />

                    {/* Analytics */}
                    <RenderIf isTrue={isSuccess && hasCoreAccess}>
                        <MenuSection
                            title={MENU_SECTIONS[2].title}
                            subsections={getAnalyticsSubsections()}
                            hasBorder={false}
                            toggleActivePopover={toggleActivePopover}
                            activePopover={activePopover}
                        />
                    </RenderIf>
                </DropdownMenu>
            )}
        </Dropdown>
    );
};

export default PowerToolsMenu;
